import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { publicMethods, baseUrl } from '../globals';
import { AuthenticationService } from './authentication.service';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { CRMLogEntry, CRMLogPage } from '../classes/types';

export const EMPTY_CRM_LOG_PAGE: CRMLogPage = {
  logs: [],
  count: 0,
  totalPages: 0,
  hasNext: false,
  hasPrevious: false,
};

/**
 * The log endpoint returned a bare array before it was paginated, and returned
 * raw `errors` before they were grouped. Accept both shapes so this can ship
 * ahead of the backend change rather than having to land at the same moment.
 */
export function normaliseCRMLogEntry(entry: any): CRMLogEntry {
  if (entry?.errorSummary) {
    return entry as CRMLogEntry;
  }

  const rawErrors: string[] = entry?.errors || [];
  return {
    ...entry,
    errorSummary: rawErrors.map(message => ({
      message,
      recordsAffected: null,
      samples: [],
    })),
    totalRecordsAffected: null,
  };
}

export function normaliseCRMLogPage(response: any): CRMLogPage {
  if (Array.isArray(response)) {
    return {
      logs: response.map(normaliseCRMLogEntry),
      count: response.length,
      totalPages: 1,
      hasNext: false,
      hasPrevious: false,
    };
  }

  if (!Array.isArray(response?.results)) {
    return EMPTY_CRM_LOG_PAGE;
  }

  return {
    logs: response.results.map(normaliseCRMLogEntry),
    count: response.count ?? response.results.length,
    totalPages: response.totalPages ?? 1,
    hasNext: !!response.next,
    hasPrevious: !!response.previous,
  };
}

@Injectable()
export class CRMIntegrationsService {
  constructor(
    private http: HttpClient,
    private pubMethods: publicMethods,
    private auth: AuthenticationService
  ) {}

  wizardLoading = signal({
    isLoadingWizardStep: false,
    isLoadingWizardStepAction: false,
    isLoadingExporter: false,
  });

  isLoading = {
    isLoadingCrmConfig: false,
    isLoadingCrmModule: false,
  };

  wizardStepsReadyState = signal({});
  configCRMType = signal('');
  configCRMSelectedObjectId = signal(null);
  specificConfigWizardStep = signal(null);
  cachedCRMObjectFields$: Observable<any> | null = null;

  buildCRMWizardEndpoint({
    stepType = '',
    actionType = '',
    objectId = null,
    fieldId = null,
    permissionsUpdateType = '',
    qParam = '',
    crmName = '',
  }): string {
    const withOptionalId = (
      base: string,
      objectId?: number | null,
      fieldId?: number | null
    ): string => {
      if (objectId && fieldId !== undefined) {
        /* NB: must pass NULL when no fieldId e.g for /object/{crmObjectPk}/field/
        * withOptionalId(`/object/`, 8, 400) => /object/8/field/400/
          withOptionalId(`/object/`, 8, null) => /object/8/field/
          withOptionalId(`/object/`, 8) => /object/8/
        */
        return fieldId
          ? `${base}${objectId}/field/${fieldId}/`
          : `${base}${objectId}/field/`;
      }

      return objectId ? `${base}${objectId}/` : base;
    };

    const getPermissionsEndpoint = () => {
      /**
       * NB: objectId passed here is NOT the usual crmObjectId
       * it's actually exporterPermissionsId!
       */
      const basePath = `/crm_framework/wizard/${this.configCRMType()}/export_permissions/`;

      let restOfPath = '';
      if (objectId || permissionsUpdateType) {
        const exporterPermissionsId = objectId;
        restOfPath =
          `${exporterPermissionsId}/` +
          (permissionsUpdateType ? `${permissionsUpdateType}/` : '');
      } else {
        restOfPath = 'list_users_and_orgs/';
      }

      return `${basePath}${restOfPath}`;
    };

    const wizardActionToEndpoint = {
      get_auth_url: `/crm_framework/wizard/${this.configCRMType()}/${stepType}/get_auth_url/`,
      list_crm_object_types: `/crm_framework/crms/${this.configCRMType()}/object/list_crm_object_types/`,
      list_crm_object_fields: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/field/list_crm_field_types/${qParam || ''}`,
      list_zint_options: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/export_mapping/available_options/`,
      list_custom_columns: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/import_mapping/list_compatible_custom_columns/${qParam || ''}`,
      list_all_transformers: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/transform/available_lookup_transformers/`,
      transform: withOptionalId(
        `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/transform/`,
        fieldId
      ),
      export_mappings: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/export_mapping/`,
      export_permissions: getPermissionsEndpoint(),
      crm_object_type: withOptionalId(
        `/crm_framework/crms/${crmName || this.configCRMType()}/object/`,
        objectId
      ),
      crm_object_fields: withOptionalId(
        `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/field/`,
        fieldId
      ),
      import_mappings: withOptionalId(
        `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/import_mapping/`,
        fieldId
      ),
      list_sync_logs: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/log/`,
      enrichment_summary: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/enrichment_summary/`,
      list_matcher_field_types: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/matcher_field/list_matcher_field_types/`,
      matcher_fields: fieldId
        ? `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/matcher_field/${fieldId}/`
        : `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/matcher_field/`,
    };

    if (actionType) {
      return baseUrl + wizardActionToEndpoint[actionType];
    }

    const stepPath = `/crm_framework/wizard/${this.configCRMType()}/${stepType}/step/`;

    return baseUrl + stepPath;
  }

  updateLoadingSignal(flag: string, loadingState: boolean): void {
    this.wizardLoading.update(prev => ({ ...prev, [flag]: loadingState }));
  }

  private handleCRMActionError(operation: string, error: any): void {
    const body = error?.error;
    const message =
      this.pubMethods.extractApiErrorMessage(body) ||
      this.pubMethods.extractApiErrorMessage(error?.message) ||
      'An unknown error occurred. Please contact us for assistance.';
    const handlerError = {
      ...error,
      error: {
        ...(body && typeof body === 'object' && !Array.isArray(body)
          ? body
          : {}),
        message,
      },
    };

    this.pubMethods.handleError(operation, [], this.isLoading)(handlerError);
  }

  getConfigsForCRM(crmType: string): Observable<any> {
    this.isLoading.isLoadingCrmConfig = true;

    return this.http
      .get(
        baseUrl + `/crm_framework/wizard/${crmType}/`,
        this.auth.helperGetVerifiedGetParams()
      )
      .pipe(
        tap(data => {
          this.configCRMType.set(data['crmType']);
          data['steps'].forEach((step: Record<string, any>) => {
            this.wizardStepsReadyState.update(obj => ({
              ...obj,
              [step.stepType]: step['isReady'],
            }));
          });
        }),
        finalize(() => (this.isLoading.isLoadingCrmConfig = false)),
        catchError(
          this.pubMethods.handleError(
            'getConfigsForCRMType',
            [],
            this.isLoading
          )
        )
      );
  }

  getAvailableCRMs(): Observable<any> {
    this.isLoading.isLoadingCrmModule = true;
    return this.http
      .get(
        baseUrl + '/crm_framework/crms/',
        this.auth.helperGetVerifiedGetParams()
      )
      .pipe(
        finalize(() => {
          this.isLoading.isLoadingCrmModule = false;
        }),
        catchError(
          this.pubMethods.handleError('getAvailableCRMs', [], this.isLoading)
        )
      );
  }

  markConfigStepCompleted(stepType = '', completed = true): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStep', true);
    const param = { isCompleted: completed };
    return this.http
      .post(
        baseUrl +
          `/crm_framework/wizard/${this.configCRMType()}/${stepType}/step/`,
        param,
        this.auth.helperGetVerifiedPostDict(true)
      )
      .pipe(
        catchError(
          this.pubMethods.handleError(
            'markConfigStepCompleted',
            [],
            this.isLoading
          )
        ),
        finalize(() => {
          this.updateLoadingSignal('isLoadingWizardStep', false);
        })
      );
  }

  getWizardStepStatus(stepType?: string): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStep', true);
    const url = this.buildCRMWizardEndpoint({ stepType });

    return this.http.get(url, this.auth.helperGetVerifiedGetParams()).pipe(
      finalize(() => this.updateLoadingSignal('isLoadingWizardStep', false)),
      catchError(
        this.pubMethods.handleError('getWizardStepStatus', [], this.isLoading)
      )
    );
  }

  // NB: wrapper method for getWizardStepStatus to avoid repeated subscriptions
  getCRMConfigStepStatus(stepType: string): void {
    this.getWizardStepStatus(stepType).subscribe(data => {
      this.wizardStepsReadyState.update(state => {
        return { ...state, [stepType]: data?.isReady };
      });
    });
  }

  getCRMExistingState({
    stepType,
    actionType,
    objectId,
    fieldId,
    crmName,
  }: {
    stepType?: string;
    actionType?: string;
    objectId?: number;
    fieldId?: number;
    crmName?: string;
  }): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStepAction', true);
    let url = this.buildCRMWizardEndpoint({
      stepType,
      actionType,
      objectId,
      fieldId,
      crmName,
    });

    return this.http.get(url, this.auth.helperGetVerifiedGetParams()).pipe(
      finalize(() => {
        this.updateLoadingSignal('isLoadingWizardStepAction', false);
      }),
      catchError(
        this.pubMethods.handleError('getCRMExistingState', [], this.isLoading)
      )
    );
  }

  /**
   * One page of an object's sync logs.
   *
   * Kept separate from getCRMExistingState so the paged, shape-normalised result
   * is typed rather than `any`: a single object's logs reached 4.9 MB unpaginated,
   * which the browser could not parse.
   */
  getCRMSyncLogs(objectId: number, page = 1): Observable<CRMLogPage> {
    this.updateLoadingSignal('isLoadingWizardStepAction', true);
    const url = this.buildCRMWizardEndpoint({
      actionType: 'list_sync_logs',
      objectId,
    });

    return this.http
      .get(url, this.auth.helperGetVerifiedGetParams({ page }))
      .pipe(
        map(normaliseCRMLogPage),
        finalize(() => {
          this.updateLoadingSignal('isLoadingWizardStepAction', false);
        }),
        catchError(
          this.pubMethods.handleError(
            'getCRMSyncLogs',
            EMPTY_CRM_LOG_PAGE,
            this.isLoading
          )
        )
      );
  }

  getCRMStepActionContent({
    stepType,
    actionType,
    objectId,
    refetch = false,
    qParam,
  }: {
    stepType: string;
    actionType: string;
    objectId?: number;
    refetch?: boolean;
    qParam?: string;
  }): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStepAction', true);

    if (actionType === 'list_crm_object_fields') {
      if (refetch === false && this.cachedCRMObjectFields$) {
        this.updateLoadingSignal('isLoadingWizardStepAction', false);
        return this.cachedCRMObjectFields$;
      }
    }

    const url = this.buildCRMWizardEndpoint({
      stepType,
      actionType,
      objectId,
      qParam,
    });

    const crmActionContent$ = this.http
      .get(url, this.auth.helperGetVerifiedGetParams())
      .pipe(
        finalize(() =>
          this.updateLoadingSignal('isLoadingWizardStepAction', false)
        ),
        catchError(
          this.pubMethods.handleError(
            'getCRMStepActionContent',
            [],
            this.isLoading
          )
        ),
        shareReplay() //for cache
      );

    if (actionType === 'list_crm_object_fields' && !qParam) {
      // only cache if NO qParam: ?zint_field_type=Text
      this.cachedCRMObjectFields$ = crmActionContent$;
    }

    return crmActionContent$;
  }

  postCRMStepActionJSON({
    postActionType,
    params,
    objectId,
    permissionsUpdateType,
  }: {
    postActionType: string;
    params: Record<string, any>;
    objectId?: number;
    permissionsUpdateType?: string;
  }): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStepAction', true);
    const url = this.buildCRMWizardEndpoint({
      actionType: postActionType,
      objectId,
      permissionsUpdateType,
    });

    return this.http
      .post(url, params, {
        ...this.auth.helperGetVerifiedPostDict(true),
        observe: 'response',
      })
      .pipe(
        finalize(() =>
          this.updateLoadingSignal('isLoadingWizardStepAction', false)
        ),
        catchError(error => {
          this.handleCRMActionError('postCRMStepActionContent', error);
          return of({ error });
        })
      );
  }

  updateCRMStepActionContent({
    updateActionType,
    params,
    objectId,
    fieldId,
    updateMethod = 'put',
  }: {
    updateActionType: string;
    params?: Record<string, any>;
    objectId: number;
    fieldId?: number;
    updateMethod?: 'put' | 'patch' | 'delete';
  }): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStepAction', true);
    const url = this.buildCRMWizardEndpoint({
      actionType: updateActionType,
      objectId,
      fieldId,
    });

    let updateOb$: Observable<any> = of(null);

    if (updateMethod === 'delete') {
      updateOb$ = this.http.delete(url, {
        ...this.auth.helperGetVerifiedDeleteDict(objectId),
        observe: 'response',
      });
    } else {
      // put or patch with params
      updateOb$ = this.http[updateMethod](url, params, {
        ...this.auth.helperGetVerifiedPutPatchDict(),
        observe: 'response',
      });
    }

    return updateOb$.pipe(
      finalize(() =>
        this.updateLoadingSignal('isLoadingWizardStepAction', false)
      ),
      catchError(error => {
        this.handleCRMActionError('putOrPatchCRMStepActionContent', error);
        return of({ error });
      })
    );
  }

  deleteCRMRecordObject(
    deleteActionType: string,
    objectId: number
  ): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStepAction', true);
    const url = this.buildCRMWizardEndpoint({
      actionType: deleteActionType,
      objectId,
    });
    return this.http
      .delete(url, {
        ...this.auth.helperGetVerifiedDeleteDict(objectId),
        observe: 'response',
      })
      .pipe(
        finalize(() =>
          this.updateLoadingSignal('isLoadingWizardStepAction', false)
        ),
        catchError(
          this.pubMethods.handleError(
            'deleteCRMRecordObject',
            [],
            this.isLoading
          )
        )
      );
  }

  deleteConfigStep(stepType = ''): Observable<any> {
    /*NB: use only when CRM object changes
     * no catchError as we expect some 404s here
     */
    return this.http
      .delete(
        baseUrl +
          `/crm_framework/wizard/${this.configCRMType()}/${stepType}/step`,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(catchError(error => of([])));
  }

  updateCRMObject(
    objectId: number,
    params: Record<string, any>
  ): Observable<any> {
    const url = `${baseUrl}/crm_framework/crms/${this.configCRMType()}/object/${objectId}/`;
    return this.http
      .patch(url, params, {
        ...this.auth.helperGetVerifiedPutPatchDict(),
        observe: 'response',
      })
      .pipe(
        catchError(
          this.pubMethods.handleError('updateCRMObject', [], this.isLoading)
        )
      );
  }

  getCRMObjectDetails(objectId: number): Observable<any> {
    const url = `${baseUrl}/crm_framework/crms/${this.configCRMType()}/object/${objectId}/`;
    return this.http.get(url, this.auth.helperGetVerifiedGetParams());
  }

  triggerCompanyMatcher(objectId: number): Observable<any> {
    const url = `${baseUrl}/crm_framework/crms/${this.configCRMType()}/object/${objectId}/trigger_company_matcher/`;
    return this.http.post(
      url,
      {},
      {
        ...this.auth.helperGetVerifiedPostDict(),
        observe: 'response',
      }
    );
  }

  getCompanyMatcherStatus(objectId: number): Observable<any> {
    const url = `${baseUrl}/crm_framework/crms/${this.configCRMType()}/object/${objectId}/company_matcher_status/`;
    return this.http.get(url, this.auth.helperGetVerifiedGetParams());
  }

  /* ------crm export services-------- */

  getCRMExportSettings({
    objectId,
    crmName,
    companyIds,
    searchQParams,
  }: {
    crmName: string;
    objectId: number;
    companyIds?: string[];
    searchQParams?: Record<string, any>;
  }): Observable<any> {
    this.updateLoadingSignal('isLoadingExporter', true);

    let jsonBody: Record<string, any>;
    if (this.pubMethods.hasData(companyIds)) {
      jsonBody = { companyIds };
    } else if (this.pubMethods.hasData(searchQParams)) {
      jsonBody = searchQParams;
    }

    const url =
      baseUrl +
      `/crm_framework/crms/${crmName.toLowerCase()}/object/${objectId}/export/${companyIds?.length ? 'search_by_company_ids' : 'search_by_query'}/`;

    return this.http
      .post(url, jsonBody, this.auth.helperGetVerifiedPostDict(true))
      .pipe(
        finalize(() => this.updateLoadingSignal('isLoadingExporter', false)),
        catchError(
          this.pubMethods.handleError(
            'getCRMExporterSettings',
            [],
            this.isLoading
          )
        )
      );
  }

  updateCRMRecords({
    objectId,
    crmName,
    payloadObj,
  }: {
    objectId: number;
    crmName: string;
    payloadObj: Record<string, {}>;
  }): Observable<any> {
    if (!this.pubMethods.hasData(payloadObj)) return;

    this.updateLoadingSignal('isLoadingExporter', true);

    const url =
      baseUrl +
      `/crm_framework/crms/${crmName.toLowerCase()}/object/${objectId}/export/update/`;

    return this.http
      .post(url, payloadObj, {
        ...this.auth.helperGetVerifiedPostDict(true),
        observe: 'response',
      })
      .pipe(
        finalize(() => this.updateLoadingSignal('isLoadingExporter', false)),
        catchError(
          this.pubMethods.handleError('updateCRMRecords', [], this.isLoading)
        )
      );
  }

  createNewCRMRecords({
    crmName,
    objectId,
    payloadObj,
  }: {
    crmName: string;
    objectId: number;
    payloadObj: Record<string, string[]>;
  }): Observable<any> {
    if (!this.pubMethods.hasData(payloadObj)) return;

    this.updateLoadingSignal('isLoadingExporter', true);

    const url =
      baseUrl +
      `/crm_framework/crms/${crmName.toLowerCase()}/object/${objectId}/export/create/`;

    return this.http
      .post(url, payloadObj, {
        ...this.auth.helperGetVerifiedPostDict(true),
        observe: 'response',
      })
      .pipe(
        finalize(() => this.updateLoadingSignal('isLoadingExporter', false)),
        catchError(
          this.pubMethods.handleError('updateCRMRecords', [], this.isLoading)
        )
      );
  }
}
